import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Phone, Mail, Users, Star, MessageSquare, Eye, EyeOff } from "lucide-react";
import { API } from "@/constants/config";

const TOKEN_KEY = "fp-admin-token";

const formatDate = (iso) =>
    new Date(iso).toLocaleString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const Stars = ({ n }) => (
    <span className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, s) => (
            <Star key={s} className={`h-4 w-4 ${s < n ? "fill-gold text-gold" : "text-ink/15"}`} />
        ))}
    </span>
);

export default function AdminPage() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("leads");
    const [leads, setLeads] = useState(null);
    const [reviews, setReviews] = useState(null);

    const authHeaders = (tk) => ({ headers: { Authorization: `Bearer ${tk || token}` } });

    const handleAuthError = (e) => {
        if (e.response?.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setLeads(null);
            setReviews(null);
            return true;
        }
        return false;
    };

    const fetchLeads = async (tk) => {
        try {
            const { data } = await axios.get(`${API}/leads`, authHeaders(tk));
            setLeads(data);
        } catch (e) {
            if (!handleAuthError(e)) toast.error("No se pudieron cargar los prospectos");
        }
    };

    const fetchReviews = async (tk) => {
        try {
            const { data } = await axios.get(`${API}/reviews`, authHeaders(tk));
            setReviews(data);
        } catch (e) {
            if (!handleAuthError(e)) toast.error("No se pudieron cargar las reseñas");
        }
    };

    useEffect(() => {
        if (token) {
            fetchLeads();
            fetchReviews();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/admin/login`, { password });
            localStorage.setItem(TOKEN_KEY, data.token);
            setToken(data.token);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Error de acceso");
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setLeads(null);
        setReviews(null);
    };

    const toggleApprove = async (reviewId) => {
        try {
            await axios.patch(`${API}/reviews/${reviewId}/approve`, {}, authHeaders());
            fetchReviews();
        } catch (e) {
            if (!handleAuthError(e)) toast.error("No se pudo actualizar la reseña");
        }
    };

    if (!token) {
        return (
            <div className="grain flex min-h-screen items-center justify-center bg-ivory px-5">
                <form
                    onSubmit={login}
                    data-testid="admin-login-form"
                    className="w-full max-w-sm border border-gold/30 bg-white p-10 shadow-xl"
                >
                    <div className="mb-8 flex flex-col items-center text-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-t-full border border-gold/60 bg-burgundy font-serif text-xl text-ivory">
                            FP
                        </span>
                        <h1 className="mt-4 font-serif text-3xl font-light text-ink">
                            Panel de Prospectos
                        </h1>
                        <p className="mt-1 text-xs uppercase tracking-[0.3em] text-ink/50">
                            FP Legacy Insurance
                        </p>
                    </div>
                    <label htmlFor="admin-password" className="text-xs uppercase tracking-[0.25em] text-ink/60">
                        Clave de acceso
                    </label>
                    <div className="relative mt-1">
                        <Lock className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                        <input
                            id="admin-password"
                            data-testid="admin-password-input"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="w-full border-b border-ink/20 bg-transparent py-3 pl-7 text-base text-ink outline-none transition-colors duration-300 focus:border-burgundy"
                        />
                    </div>
                    {error && (
                        <p data-testid="admin-login-error" className="mt-4 text-sm text-burgundy">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-button"
                        className="mt-8 w-full rounded-full bg-burgundy py-3.5 text-sm tracking-wide text-ivory transition-[background-color,opacity] duration-300 hover:bg-forest disabled:opacity-60"
                    >
                        {loading ? "Entrando..." : "Entrar"}
                    </button>
                </form>
            </div>
        );
    }

    const pendingCount = reviews ? reviews.filter((r) => !r.approved).length : 0;

    return (
        <div className="min-h-screen bg-ivory">
            <header className="border-b border-gold/25 bg-burgundy text-ivory">
                <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-5 md:px-8">
                    <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-t-full border border-gold/60 font-serif text-lg">
                            FP
                        </span>
                        <div>
                            <p className="font-serif text-xl leading-none">Panel de Prospectos</p>
                            <p className="text-[10px] uppercase tracking-[0.3em] text-ivory/60">
                                FP Legacy Insurance
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="admin-refresh-button"
                            onClick={() => {
                                fetchLeads();
                                fetchReviews();
                            }}
                            className="flex items-center gap-2 rounded-full border border-ivory/30 px-4 py-2 text-sm transition-colors duration-300 hover:border-gold hover:text-gold"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Actualizar
                        </button>
                        <button
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="flex items-center gap-2 rounded-full border border-ivory/30 px-4 py-2 text-sm transition-colors duration-300 hover:border-gold hover:text-gold"
                        >
                            <LogOut className="h-4 w-4" />
                            Salir
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-5 py-10 md:px-8" data-testid="admin-panel">
                <div className="mb-8 flex flex-wrap items-center gap-3">
                    <button
                        data-testid="admin-tab-leads"
                        onClick={() => setTab("leads")}
                        className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm tracking-wide transition-colors duration-300 ${
                            tab === "leads"
                                ? "bg-burgundy text-ivory"
                                : "border border-ink/20 text-ink/60 hover:border-burgundy hover:text-burgundy"
                        }`}
                    >
                        <Users className="h-4 w-4" />
                        Prospectos {leads ? `(${leads.length})` : ""}
                    </button>
                    <button
                        data-testid="admin-tab-reviews"
                        onClick={() => setTab("reviews")}
                        className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm tracking-wide transition-colors duration-300 ${
                            tab === "reviews"
                                ? "bg-burgundy text-ivory"
                                : "border border-ink/20 text-ink/60 hover:border-burgundy hover:text-burgundy"
                        }`}
                    >
                        <MessageSquare className="h-4 w-4" />
                        Reseñas {reviews ? `(${reviews.length})` : ""}
                        {pendingCount > 0 && (
                            <span
                                data-testid="reviews-pending-badge"
                                className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-medium text-burgundy-deep"
                            >
                                {pendingCount} nueva{pendingCount === 1 ? "" : "s"}
                            </span>
                        )}
                    </button>
                </div>

                {tab === "leads" && (
                    <>
                        {leads && leads.length === 0 && (
                            <p data-testid="leads-empty" className="border border-gold/25 bg-white p-10 text-center text-ink/60">
                                Aún no hay prospectos. Cuando alguien complete el formulario, aparecerá aquí.
                            </p>
                        )}
                        <div className="space-y-4" data-testid="leads-list">
                            {leads?.map((lead, i) => (
                                <article
                                    key={lead.id}
                                    data-testid={`lead-item-${i}`}
                                    className="border border-gold/25 bg-white p-6 transition-shadow duration-300 hover:shadow-lg"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <h2 className="font-serif text-2xl text-ink">{lead.nombre}</h2>
                                            <p className="mt-0.5 text-xs uppercase tracking-[0.2em] text-ink/45">
                                                {formatDate(lead.created_at)}
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <a
                                                href={`tel:${lead.telefono}`}
                                                data-testid={`lead-phone-${i}`}
                                                className="flex items-center gap-2 rounded-full border border-burgundy/30 px-4 py-2 text-sm text-burgundy transition-colors duration-300 hover:bg-burgundy hover:text-ivory"
                                            >
                                                <Phone className="h-4 w-4" />
                                                {lead.telefono}
                                            </a>
                                            <a
                                                href={`mailto:${lead.email}`}
                                                data-testid={`lead-email-${i}`}
                                                className="flex items-center gap-2 rounded-full border border-burgundy/30 px-4 py-2 text-sm text-burgundy transition-colors duration-300 hover:bg-burgundy hover:text-ivory"
                                            >
                                                <Mail className="h-4 w-4" />
                                                {lead.email}
                                            </a>
                                        </div>
                                    </div>
                                    {lead.mensaje && (
                                        <p className="mt-4 border-t border-gold/20 pt-4 text-sm leading-relaxed text-ink/70">
                                            “{lead.mensaje}”
                                        </p>
                                    )}
                                </article>
                            ))}
                        </div>
                    </>
                )}

                {tab === "reviews" && (
                    <>
                        {reviews && reviews.length === 0 && (
                            <p data-testid="reviews-empty" className="border border-gold/25 bg-white p-10 text-center text-ink/60">
                                Aún no hay reseñas. Comparte tu enlace /resena con tus clientes.
                            </p>
                        )}
                        <div className="space-y-4" data-testid="reviews-list">
                            {reviews?.map((review, i) => (
                                <article
                                    key={review.id}
                                    data-testid={`review-item-${i}`}
                                    className="border border-gold/25 bg-white p-6 transition-shadow duration-300 hover:shadow-lg"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <h2 className="font-serif text-2xl text-ink">{review.nombre}</h2>
                                            <div className="mt-1 flex items-center gap-3">
                                                <Stars n={review.rating} />
                                                <p className="text-xs uppercase tracking-[0.2em] text-ink/45">
                                                    {review.ubicacion || "—"} · {formatDate(review.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            data-testid={`review-approve-${i}`}
                                            onClick={() => toggleApprove(review.id)}
                                            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors duration-300 ${
                                                review.approved
                                                    ? "bg-forest text-ivory hover:bg-burgundy"
                                                    : "border border-forest/40 text-forest hover:bg-forest hover:text-ivory"
                                            }`}
                                        >
                                            {review.approved ? (
                                                <>
                                                    <EyeOff className="h-4 w-4" />
                                                    Publicada — ocultar
                                                </>
                                            ) : (
                                                <>
                                                    <Eye className="h-4 w-4" />
                                                    Aprobar y publicar
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <p className="mt-4 border-t border-gold/20 pt-4 font-serif text-lg italic leading-relaxed text-ink/75">
                                        “{review.resena}”
                                    </p>
                                </article>
                            ))}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
